import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  contactLawyer,
  singleLawyer,
} from "../../redux/actions/lawyer_actions";
import Loader from "../../common/loader/Loader";
import { LAWYER_MESSAGE_DATA } from "../../constants/constants";
import InputText from "../../common/form/InputText";
import InputArea from "../../common/form/InputArea";
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Typography,
} from "@material-tailwind/react";
import DetailItem from "../../common/shared/DetailItem";
import moment from "moment";
import CorrectSVG from "../../common/shared/CorrectSVG";
import arrSeparator from "../../utils/arrSeparator";
import Cookies from "js-cookie";

function SingleLawyer() {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(LAWYER_MESSAGE_DATA);
  const [sendLoading, setSendLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [userData, setUserData] = useState(null);

  const dispatch = useDispatch();
  const { single } = useSelector((state) => state.lawyer);

  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };

  // Helper function to check visibility based on user role
  const isFieldVisible = (publicSetting, glcSetting, lawyerSetting) => {
    if (!single) return false;
    if (userData === null) {
      return publicSetting || !single?.settings;
    }
    return userData?.user?.isGLC ? glcSetting : lawyerSetting;
  };

  const formatDate = (value) => {
    if (!value) return null;
    const formatted = moment(value).format("DD/MM/YYYY");
    return formatted === "Invalid date" ? null : formatted;
  };

  const chamberItems = Array.isArray(single?.chamber)
    ? single.chamber.filter((item) => hasValue(item))
    : hasValue(single?.chamber)
      ? [single.chamber]
      : [];

  const chamberRegionItems = [
    chamberItems.length > 0 && {
      title: "Chamber Name",
      item: chamberItems.join(", "),
    },
    hasValue(single?.region) && {
      title: "Region",
      item: single.region,
    },
  ].filter(Boolean);

  // Build array of ALL visible detail items in one unified section
  const allDetailsItems = [
    isFieldVisible(
      single?.settings?.public?.bar_number || !single?.settings,
      single?.settings?.GLC?.bar_number,
      single?.settings?.lawyers?.bar_number,
    ) && { title: "Bar Number", item: single?.bar_number },
    isFieldVisible(
      single?.settings?.public?.enrolment_date || !single?.settings,
      single?.settings?.GLC?.enrolment_date,
      single?.settings?.lawyers?.enrolment_date,
    ) && {
      title: "Date Of Call",
      item: formatDate(single?.doc),
    },
    userData?.user?.isGLC && {
      title: "Date of birth",
      item: formatDate(single?.dob),
    },
    isFieldVisible(
      single?.settings?.public?.gender || !single?.settings,
      single?.settings?.GLC?.gender,
      single?.settings?.lawyers?.gender,
    ) && {
      title: "Gender",
      item:
        typeof single?.gender === "string" ? single.gender.toUpperCase() : null,
    },
    isFieldVisible(
      single?.settings?.public?.email || !single?.settings,
      single?.settings?.GLC?.email,
      single?.settings?.lawyers?.email,
    ) && { title: "Email Address", item: single?.email },
    isFieldVisible(
      single?.settings?.public?.phone || !single?.settings,
      single?.settings?.GLC?.phone,
      single?.settings?.lawyers?.phone,
    ) && { title: "Phone", item: single?.phone },
    isFieldVisible(
      single?.settings?.public?.academic_q || !single?.settings,
      single?.settings?.GLC?.academic_q,
      single?.settings?.lawyers?.academic_q,
    ) && { title: "Academic Qualifications", item: single?.qua },
    isFieldVisible(
      single?.settings?.public?.practice_areas || !single?.settings,
      single?.settings?.GLC?.practice_areas,
      single?.settings?.lawyers?.practice_areas,
    ) && {
      title: "Practice Areas",
      item: arrSeparator({ arr: single?.practice }),
    },
  ].filter((field) => field && hasValue(field.item));

  const updateFormValue = ({ updateType, value }) => {
    setFormData({ ...formData, [updateType]: value });
  };

  const handleOpen = () => setOpen(!open);

  const handleSendMessage = (e) => {
    e.preventDefault();
    setSendLoading(true);
    const sendData = {
      id: single?.email,
      first_name: formData.first_name,
      last_name: formData.last_name,
      message: formData.message,
      email: formData.email,
    };
    dispatch(contactLawyer(sendData)).finally(() => {
      setSendLoading(false);
      setOpen(true);
      setFormData(LAWYER_MESSAGE_DATA);
    });
  };

  useEffect(() => {
    dispatch(singleLawyer(id)).finally(() => setLoading(false));
  }, [dispatch, id]);

  useEffect(() => {
    const userCookie = Cookies.get("dataCookie");

    if (!userCookie) {
      setUserData(null);
      return;
    }

    try {
      const parsedData = JSON.parse(userCookie);
      setUserData(parsedData);
    } catch {
      setUserData(null);
    }
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="bg-[#EFEFF1] h-screen">
          <div className="flex items-center shadow-xl px-3 md:px-10 lg:15 xl:px-20 banner">
            <h2 className="px-2 md:px-10 lg:15 xl:px-20 text-[2rem] md:text-[3rem] text-white font-bold">
              {single?.title + " " + single?.name}
            </h2>
          </div>

          <div className="px-2 pt-10 md:px-10 lg:15 xl:px-20 bg-[#EFEFF1]  py-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-20 justify-between w-full">
              <div className="lg:col-span-2 flex flex-col gap-3">
                {chamberRegionItems.length > 0 && (
                  <div
                    className={`grid grid-cols-1 ${chamberRegionItems.length > 1 ? "md:grid-cols-2" : "md:grid-cols-1"} gap-4 my-4`}
                  >
                    {chamberRegionItems.map((field) => (
                      <DetailItem
                        key={field.title}
                        title={field.title}
                        item={field.item}
                      />
                    ))}
                  </div>
                )}

                {allDetailsItems.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
                    {allDetailsItems.map((field, idx) => (
                      <DetailItem
                        key={idx}
                        title={field.title}
                        item={field.item}
                      />
                    ))}
                  </div>
                )}
              </div>

              <form onSubmit={(e) => handleSendMessage(e)}>
                <div className="p-8 bg-white  h-fit shadow-lg rounded-md">
                  <Typography variant="h4" className="mb-4">
                    Get In Touch
                  </Typography>
                  <div className=" flex flex-col justify-between gap-6 w-full">
                    <InputText
                      defaultValue={formData.first_name}
                      updateType="first_name"
                      labelTitle="First Name"
                      updateFormValue={updateFormValue}
                      required={true}
                    />

                    <InputText
                      defaultValue={formData.last_name}
                      updateType="last_name"
                      labelTitle="Last Name"
                      updateFormValue={updateFormValue}
                      required={true}
                    />

                    <InputText
                      defaultValue={formData.email}
                      updateType="email"
                      labelTitle="Email Address"
                      updateFormValue={updateFormValue}
                      required={true}
                    />

                    <InputArea
                      defaultValue={formData.message}
                      updateType="message"
                      labelTitle="Message"
                      updateFormValue={updateFormValue}
                      required={true}
                    />
                  </div>
                  <Button
                    type="submit"
                    fullWidth
                    color="pink"
                    className="mt-6"
                    loading={sendLoading}
                  >
                    Send Message
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {open && (
        <Dialog open={open} handler={handleOpen}>
          <DialogHeader className="grid place-items-center gap-4">
            <Typography variant="h5" color="blue-gray">
              Message Sent Successfully
            </Typography>
          </DialogHeader>
          <DialogBody className="grid place-items-center gap-4">
            <CorrectSVG />
          </DialogBody>
          <DialogFooter>
            <Button variant="text" color="blue-gray" onClick={handleOpen}>
              close
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
}

export default SingleLawyer;
